import type { CatalogUnit, Emitter } from "./types";

const HTML_TAG = /<[^>]*>/g;
const HTML_ENTITY: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

function stripHtml(value: string): string {
  return value
    .replace(HTML_TAG, " ")
    .replace(/&[a-z#0-9]+;/gi, (entity) => HTML_ENTITY[entity] ?? entity)
    .replace(/\s+/g, " ")
    .trim();
}

function asString(value: unknown, fieldType: string): string | undefined {
  if (value == null) return undefined;

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : undefined;
  }

  if (typeof value === "boolean") return String(value);
  if (typeof value !== "string") return undefined;

  const text = fieldType === "rich_text" ? stripHtml(value) : value.trim();

  return text.length > 0 ? text : undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== "string") return undefined;

  const cleaned = value.trim().replace(/\./g, "").replace(",", ".");
  if (cleaned.length === 0) return undefined;

  const parsed = Number(cleaned);

  return Number.isFinite(parsed) ? parsed : undefined;
}

function srcOf(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value.trim().length > 0 ? value.trim() : undefined;
  }

  if (value == null || typeof value !== "object") return undefined;

  const record = value as Record<string, any>;
  const candidate =
    record.src ?? record.image?.desktop?.src ?? record.image?.mobile?.src;

  return typeof candidate === "string" && candidate.trim().length > 0
    ? candidate.trim()
    : undefined;
}

/** Texto puro. `rich_text` perde a marcação — schema.org espera texto, não HTML. */
export function text(): Emitter {
  return ({ value, fieldType }) => asString(value, fieldType);
}

/** Número. Aceita string com separador brasileiro (`1.234,50`). */
export function number(): Emitter {
  return ({ value }) => asNumber(value);
}

/** Inteiro. Trunca em vez de arredondar — portas e assentos não têm meio. */
export function integer(): Emitter {
  return ({ value }) => {
    const parsed = asNumber(value);

    return parsed === undefined ? undefined : Math.trunc(parsed);
  };
}

// Data e hora. `datetime` mantém a hora; `date` descarta.
export function date({ keepTime = false }: { keepTime?: boolean } = {}): Emitter {
  return ({ value, fieldType }) => {
    if (typeof value === "number" && Number.isInteger(value)) {
      return value >= 1000 && value <= 9999 ? String(value) : undefined;
    }

    const raw = asString(value, fieldType);
    if (raw === undefined) return undefined;

    if (/^\d{4}$/.test(raw)) return raw;

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return undefined;

    if (keepTime && fieldType === "datetime") return raw;

    return parsed.toISOString().slice(0, 10);
  };
}

/** Lista de URLs de imagem, a partir de imagem única, galeria ou mídia. */
export function imageList(): Emitter {
  return ({ value }) => {
    const items = Array.isArray(value) ? value : [value];
    const urls = items
      .map(srcOf)
      .filter((src): src is string => src !== undefined);

    return urls.length > 0 ? urls : undefined;
  };
}

/** Lista de textos, a partir de array ou de string separada por `;` ou `,`. */
export function stringList(): Emitter {
  return ({ value, fieldType }) => {
    const items = Array.isArray(value)
      ? value
      : typeof value === "string"
        ? value.split(/[;,]/)
        : [];

    const texts = items
      .map((item) =>
        typeof item === "object" && item !== null
          ? asString((item as Record<string, unknown>).value, fieldType)
          : asString(item, fieldType),
      )
      .filter((item): item is string => item !== undefined);

    return texts.length > 0 ? texts : undefined;
  };
}

/**
 * URL de um enum do schema.org. O `valueMap` configurado no admin traduz o valor
 * do CMS para a chave da receita; sem correspondência, o valor bruto ainda é
 * tentado como chave, o que cobre o caso de o rótulo já ser a própria chave.
 */
export function enumUrl(members: Record<string, string>): Emitter {
  return ({ value, fieldType, valueMap }) => {
    const raw =
      typeof value === "boolean"
        ? String(value)
        : asString(value, fieldType)?.toLowerCase();

    if (raw === undefined) return undefined;

    const key = valueMap?.[raw] ?? (raw in members ? raw : undefined);
    if (key === undefined) return undefined;

    const member = members[key];

    return member ? `https://schema.org/${member}` : undefined;
  };
}

/** Nó `Brand`. Resolve `brand_id` pelo catálogo de marcas; senão, usa o texto. */
export function brandName(): Emitter {
  return ({ value, fieldType, resolveBrand }) => {
    if (fieldType === "brand_id") {
      const id = asString(value, fieldType);
      const resolved = id ? resolveBrand?.(id) : null;
      const name = resolved?.name?.trim();

      return name ? name : undefined;
    }

    return asString(value, fieldType);
  };
}

/**
 * Referência à unidade pelo `@id` que o Card 1 já emite. Devolve só o ponteiro:
 * o nó completo do `AutoDealer` é responsabilidade do builder de unidades.
 */
export function unitReference(): Emitter {
  return ({ value, fieldType, resolveUnit, unitReferenceId }) => {
    const id = asString(value, fieldType);
    if (id === undefined) return undefined;

    const unit: CatalogUnit | null | undefined = resolveUnit?.(id);
    if (!unit) return undefined;

    const reference = unitReferenceId?.(unit);

    return reference ? { "@id": reference } : undefined;
  };
}

/** Nó `Person` a partir do nome. Autor da publicação. */
export function personName(): Emitter {
  return ({ value, fieldType }) => asString(value, fieldType);
}
